import StaticPage from "@/components/StaticPage";

export default function About() {
  return (
    <StaticPage eyebrow="About us" title="Turn every visit into the next one." updatedAt="February 2026">
      <p className="text-lg text-[#183D36]">
        NextVisit started with a simple observation: the best appointment-based businesses aren&apos;t the ones with the biggest ad budgets. They&apos;re the ones that make you feel remembered the day after you leave.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">The problem we&apos;re solving</h2>
      <p>
        Small salons, med spas, dental practices, wellness studios, groomers, mechanics, and independent coaches all share the same quiet reality: <em>50–70% of their revenue depends on repeat visits</em>. Yet the moment a customer walks out the door, most of these businesses go silent — no thank-you, no review request, no gentle rebook nudge, no win-back when someone drifts away.
      </p>
      <p>
        The reason isn&apos;t apathy. It&apos;s that the tools available today are built for enterprise: cluttered dashboards, per-message pricing, months to implement. The people running a five-chair salon or a two-op dental practice don&apos;t have time to be marketers on top of everything else.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">What we build</h2>
      <p>
        NextVisit is a quiet follow-up system that runs itself. Connect your booking system (or log appointments manually), pick the tone that sounds like you, and every visit automatically triggers four warm messages at exactly the right time: a thank-you, a review request, a rebook nudge, and a win-back if someone stops coming.
      </p>
      <p>
        You don&apos;t manage campaigns. You don&apos;t A/B test subject lines. You do great work with your clients, and NextVisit makes sure they remember you when it&apos;s time to come back.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Our principles</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li><b>Small teams first.</b> Every design decision is filtered through the question: would the owner of a five-person shop actually use this on a Tuesday?</li>
        <li><b>No feature bloat.</b> We say no to a lot. Fewer, better-timed messages will always beat more, worse-timed messages.</li>
        <li><b>Your voice, not ours.</b> Templates are yours to edit — no cheesy defaults. Our AI helps you draft, never dictates.</li>
        <li><b>Respect the inbox.</b> Every message has an unsubscribe link, we honor it immediately, and we&apos;ll never let you send to someone who&apos;s opted out.</li>
        <li><b>Transparent pricing.</b> Flat monthly plans. No per-message fees, no surprise overages, no upsell walls.</li>
      </ul>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Get in touch</h2>
      <p>
        We&apos;re a small team building this deliberately. If you want to ask a question, share feedback, or just say hi, we&apos;d love to hear from you.
      </p>
      <p>
        Email us at <a href="mailto:brooke@bookyournextvisit.com" className="text-[#183D36] underline">brooke@bookyournextvisit.com</a> or use our <a href="/contact" className="text-[#183D36] underline">contact form</a>. We answer every message personally, usually within one business day.
      </p>
    </StaticPage>
  );
}
